/**
 * Gated patient balance worklist table (gdn-2943; gate
 * feature_patient_balance_table).
 *
 * The winning shape from the GDN-2932 prototype round: filter chips carry
 * scale; the numbers are the interface — Patient share hovers to the
 * receipt (billed − contractual adjustment − insurance paid = patient
 * share), Collected hovers to the lifecycle lines, the outcome chip hovers
 * to the backend's explanation; the whole row navigates to the encounter
 * view; actionable rows carry one inline button driven by
 * recommended_action.
 *
 * Every derivation degrades gracefully when the deployed API predates the
 * gdn-2940/2941 fields (no dotted underline when there's nothing to show).
 */

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CheckIcon from '@mui/icons-material/Check';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { EncounterLinesPanel } from './EncounterLinesPanel';
import type { CreditSourceEntry } from './rows';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Menu,
  MenuItem,
  ToggleButton,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  ACCOUNT_REMINDER_KEY,
  APPLY_CREDIT_KEY,
  ASSIGN_CHARGE_KEY,
  REMOVE_RECORDED_PAYMENT_KEY,
  BILL_HELD_CHARGE_KEY,
  WRITE_OFF_HELD_CHARGE_KEY,
  REVERSE_CHARGE_KEY,
  INVOICE_COPY_LINK_KEY,
  INVOICE_RETRY_PAYMENT_KEY,
  INVOICE_STOP_RETRIES_KEY,
  INVOICE_VOID_KEY,
  RECORD_REFUND_KEY,
  type RowActions,
  rowActionsOf,
} from './actions';
import {
  type AccountRow,
  type ActivityLine,
  assignableChargeOf,
  heldChargeOf,
  removablePaymentsOf,
  reversibleChargeOf,
  type BalanceBucket,
  type BalanceRow,
  type BalanceTableRow,
  accountChargeTargetsOf,
  type CreditEligibility,
  type CreditSource,
  creditTargetsOf,
  creditVerbsOf,
  formatCents,
  invoiceNoteDetail,
  invoiceNoteText,
  type ReceiptRow,
  type RowInvoice,
} from './rows';

const INK = '#1a1d23';
const INK_SOFT = '#4b5563';
const INK_FAINT = '#8b93a1';
const LINE = '#e5e7eb';
const LINE_STRONG = '#d3d8e0';
const BG = '#f7f8fa';
const BRAND = '#36229e';
/** True minus (U+2212) — screen readers skip parentheses and have weaker
 * support for hyphen-minus (ledger-ux research). */
const MINUS = '−';

const TONE_STYLES: Record<
  BalanceRow['outcome']['tone'],
  { color: string; bg?: string }
> = {
  quiet: { color: '#16a34a' },
  owes: { color: '#d97706', bg: '#fffbeb' },
  credit: { color: '#16a34a', bg: '#f0fdf4' },
  hold: { color: '#dc2626', bg: '#fef2f2' },
};

type Filter = 'all' | BalanceBucket;

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'attention', label: 'Needs attention' },
  { id: 'waiting', label: 'Awaiting insurance' },
  { id: 'hold', label: 'On hold' },
  { id: 'settled', label: 'Settled' },
];

/** Analytics value for the invoiced modifier, alongside the bucket ids. */
export const INVOICED_ONLY_FILTER = 'invoiced_only';

/**
 * "Already invoiced" is a second axis, not a fifth bucket. `linked_invoice_ids`
 * carries any OPEN or DRAFT invoice regardless of reconciliation state, so an
 * invoiced row can be attention (confirmed and uncollected), hold (suspended
 * for secondary with the invoice still open — the server marks those stale),
 * or settled (paid another way while the invoice sat there). It is neither a
 * subset of one bucket nor disjoint from the rest, so a chip beside chips
 * whose counts partition All read as a peer it isn't.
 *
 * As a modifier it composes instead: "of the things needing attention, which
 * have I already chased?" — which is the question, and the overlap stops
 * mattering because the control is visibly a different kind of thing.
 */
const matchesFilter = (
  row: BalanceTableRow,
  bucket: Filter,
  invoicedOnly: boolean,
): boolean => {
  if (invoicedOnly && row.invoice === null) return false;
  return bucket === 'all' || row.bucket === bucket;
};

/** Says an invoice is already out, so an "Owes" row that offers "Send
 * reminder" explains itself instead of looking like an un-actioned balance
 * with a surprising verb (gdn-3001). Renders nothing when none is out.
 *
 * A decline takes over the line (gdn-3064), and its two states carry
 * opposite weights: retries pending stays in the note's muted gray —
 * informational, Stripe very probably recovers it unattended — while
 * no-retries-left takes the owes amber, because that row is the
 * one-in-ten that goes nowhere until someone acts. */
const InvoiceNote = ({
  invoice,
  onRetry,
}: {
  invoice: RowInvoice | null;
  /** Opens the update-card-and-retry flow for this invoice (gdn-3154). */
  onRetry?: () => void;
}) => {
  if (!invoice) return null;
  const retriesExhausted =
    invoice.decline !== null && invoice.decline.nextAttemptDate === null;
  return (
    <Typography
      variant="caption"
      noWrap
      sx={{
        display: 'block',
        fontSize: '0.6875rem',
        color: retriesExhausted ? TONE_STYLES.owes.color : INK_FAINT,
        fontWeight: retriesExhausted ? 600 : 400,
        pl: '1.375rem',
        lineHeight: 1.4,
      }}
    >
      {invoiceNoteText(invoice)}
      {/* The card path was ⋮-only, which is where a biller looks last
          (gdn-3154). Shown whether Stripe is still dunning or has given
          up: a biller who knows the card is dead should not have to wait
          out the retry schedule to say so. A LINK, not a button —
          gdn-3064's emphasis decision stands, and nine declines in ten
          still resolve themselves unattended. */}
      {invoice.decline && onRetry && (
        <>
          {' · '}
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={event => {
              // The row is a link to the encounter; this is not.
              event.stopPropagation();
              onRetry();
            }}
            sx={{
              fontSize: 'inherit',
              fontWeight: 600,
              verticalAlign: 'baseline',
              color: 'inherit',
            }}
          >
            Update card &amp; retry
          </Link>
        </>
      )}
    </Typography>
  );
};

const HOVER_TOOLTIP_SX = {
  bgcolor: '#fff',
  color: INK,
  border: `1px solid ${LINE_STRONG}`,
  boxShadow: '0 4px 16px rgba(16,24,40,0.12)',
  p: 1.25,
  maxWidth: 340,
} as const;

/** A credit verb's availability for one row: the source when it can
 * run, or the reason it cannot when it applies but is blocked. Both null
 * means the verb does not apply to this row at all. */
interface CreditVerbState {
  source: CreditSource | null;
  blockedReason: string | null;
}

/** An account charge held after a refund, as its row hands it to a verb. */
export interface HeldChargeTarget {
  chargeLineId: string;
  amountCents: number;
  label: string;
}

const verbStateOf = (eligibility: CreditEligibility): CreditVerbState => ({
  source: eligibility.status === 'eligible' ? eligibility.source : null,
  blockedReason: eligibility.status === 'blocked' ? eligibility.reason : null,
});

export interface PatientBalanceTableProps {
  rows: BalanceTableRow[];
  onAction: (
    row: BalanceRow,
    action: string,
    options?: { via: 'button' | 'overflow' },
  ) => void;
  onFilterSelected?: (filter: string) => void;
  onRowClicked?: (encounterId: string) => void;
  /** Send-reminder verb for an account row riding an open invoice. */
  onAccountInvoiceReminder?: (invoiceId: string) => void;
  /** The Open Invoices panel's housekeeping, now carried by whichever row
   * rides the invoice (gdn-3001). */
  onInvoiceCopyLink?: (invoiceId: string) => void;
  onInvoiceVoid?: (invoiceId: string) => void;
  /** Declined-invoice recovery verbs (gdn-3064): update the card on file
   * and retry the charge, or switch off Stripe's automatic retries
   * without touching the debt. Offered only on rows whose invoice carries
   * a decline; see actions.ts for how the two decline states weight them. */
  onInvoiceRetryPayment?: (
    invoiceId: string,
    options?: { via: 'button' | 'overflow' },
  ) => void;
  onInvoiceStopRetries?: (invoiceId: string) => void;
  /** Repair verb for a stranded account charge (gdn-3106): move its money
   * onto the encounter it was really for. Offered only on rows
   * assignableChargeOf accepts. */
  onAssignCharge?: (charge: { chargeLineId: string; amountCents: number; label: string }) => void;
  /** Correct a payment recorded by mistake on the credit row (gdn-3255).
   * Receives the removable PAYMENT sources; the dialog picks among them. */
  onRemoveRecordedPayment?: (payments: CreditSourceEntry[]) => void;
  /** The two answers to an account charge a refund left held (gdn-4242):
   * bill the patient for it again, or write it off. Offered only on rows
   * heldChargeOf accepts, and only when both are supplied. */
  onBillHeldCharge?: (charge: HeldChargeTarget) => void;
  onWriteOffHeldCharge?: (charge: HeldChargeTarget) => void;
  /** Whether the viewer may act on this patient's money (gdn-3287).
   * Suppresses the billing recommendations; the row verbs are already
   * withheld by their handlers being absent. */
  canManagePatientFinancials?: boolean;
  /** Undo a charge that should not have been raised (gdn-3187). Offered
   * on any charge-line-backed account row — the server decides
   * eligibility, so the verb is present and may be refused, rather than
   * absent and unexplained. */
  onReverseCharge?: (charge: {
    chargeLineId: string;
    amountCents: number;
    hasAppliedPayment?: boolean;
    label: string;
    encounterId?: string;
  }) => void;
  /** Row click-through for an account row riding an open invoice. */
  onAccountInvoiceOpen?: (invoiceId: string) => void;
  /** Allocation verb (gdn-2950): a credit row's "Apply credit" button.
   * Rendered only when the row holds a reconciled credit AND at least one
   * reconciled encounter owes. */
  onApplyCredit?: (
    source: CreditSource,
    options?: { via: 'button' | 'overflow' },
  ) => void;
  /** Refund verb (gdn-2978): eligible wherever Apply credit's source is —
   * but with no target requirement, so a credit row with nothing to apply
   * to still resolves (Refund becomes its primary). */
  onRecordRefund?: (
    source: CreditSource,
    options?: { via: 'button' | 'overflow' },
  ) => void;
  /** The BE's allocatable account credit — gross payments minus the
   * account's own open charges. Gates and caps the ACCOUNT credit row's
   * verb; absent (older BE) hides it rather than offering the gross. */
  accountAllocatableCreditCents?: number | null;
  /** The account's unapplied credit (summary.credit_balance_cents): what
   * the credit row can pay the account's own charges with (gdn-4321).
   * Absent (older BE) offers no account charge. */
  accountUnappliedCreditCents?: number | null;
  /** Subject-language capitalized singular ("Patient" | "Client") — a prop
   * so the table stays presentational (no redux in Storybook). */
  subjectCap?: string;
}

export const PatientBalanceTable: React.FC<PatientBalanceTableProps> = ({
  rows,
  onAction,
  onFilterSelected,
  onRowClicked,
  onAccountInvoiceReminder,
  onAccountInvoiceOpen,
  onInvoiceCopyLink,
  onInvoiceVoid,
  onInvoiceRetryPayment,
  onInvoiceStopRetries,
  onAssignCharge,
  onReverseCharge,
  onRemoveRecordedPayment,
  onBillHeldCharge,
  onWriteOffHeldCharge,
  canManagePatientFinancials,
  onApplyCredit,
  onRecordRefund,
  accountAllocatableCreditCents = null,
  accountUnappliedCreditCents = null,
  subjectCap = 'Patient',
}) => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');
  const [invoicedOnly, setInvoicedOnly] = useState(false);

  // Where each credit verb can put the money (gdn-4321): visits that owe
  // and, for the account's credit, its own open charges. The refund verb
  // only needs the credit itself.
  const creditContext = useMemo(
    () => ({
      allocatableCents: accountAllocatableCreditCents,
      unappliedCents: accountUnappliedCreditCents,
      visitTargets: creditTargetsOf(rows),
      accountCharges: accountChargeTargetsOf(rows),
    }),
    [rows, accountAllocatableCreditCents, accountUnappliedCreditCents],
  );
  /** Apply credit needs both a movable credit and somewhere to move it.
   * A row that holds credit but cannot move it says why rather than
   * dropping the verb (gdn-2994). */
  const applyCreditFor = (row: BalanceTableRow): CreditVerbState =>
    onApplyCredit
      ? verbStateOf(creditVerbsOf(row, creditContext).apply)
      : { source: null, blockedReason: null };
  /** Refund needs the credit itself — no destination visit required. */
  const recordRefundFor = (row: BalanceTableRow): CreditVerbState =>
    onRecordRefund
      ? verbStateOf(creditVerbsOf(row, creditContext).refund)
      : { source: null, blockedReason: null };

  const visible = useMemo(
    () => rows.filter(row => matchesFilter(row, filter, invoicedOnly)),
    [rows, filter, invoicedOnly],
  );
  // Bucket counts honour the modifier, so a chip never promises more rows
  // than clicking it would show.
  const countOf = (id: Filter): number =>
    rows.filter(row => matchesFilter(row, id, invoicedOnly)).length;
  // The modifier counts within the current bucket, for the same reason.
  const invoicedCount = rows.filter(
    row => matchesFilter(row, filter, true),
  ).length;

  const openEncounter = (row: BalanceRow) => {
    onRowClicked?.(row.encounterId);
    navigate(`/jotbill/encounter/${row.encounterId}`);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
        {FILTERS.map(option => (
          <Chip
            key={option.id}
            label={`${option.label} (${countOf(option.id)})`}
            size="small"
            onClick={() => {
              setFilter(option.id);
              onFilterSelected?.(option.id);
            }}
            sx={{
              fontWeight: 600,
              fontSize: '0.7rem',
              color: filter === option.id ? '#fff' : INK_SOFT,
              bgcolor: filter === option.id ? BRAND : '#eef0f3',
              '&:hover': { bgcolor: filter === option.id ? BRAND : LINE_STRONG },
            }}
          />
        ))}
        {/* Pushed to the far end and shaped as an option rather than a
            chip: it filters *within* whatever bucket is selected, so it
            must not read as a fifth bucket. */}
        <Box sx={{ flex: 1, minWidth: 8 }} />
        <ToggleButton
          value="invoiced"
          size="small"
          selected={invoicedOnly}
          disabled={invoicedCount === 0 && !invoicedOnly}
          onChange={() => {
            const next = !invoicedOnly;
            setInvoicedOnly(next);
            if (next) onFilterSelected?.(INVOICED_ONLY_FILTER);
          }}
          sx={{
            textTransform: 'none',
            fontSize: '0.7rem',
            fontWeight: 600,
            py: 0.25,
            px: 1,
            gap: 0.5,
            border: `1px solid ${LINE_STRONG}`,
            color: INK_SOFT,
            '&.Mui-selected': { bgcolor: BRAND, color: '#fff' },
            '&.Mui-selected:hover': { bgcolor: BRAND },
          }}
        >
          <CheckIcon sx={{ fontSize: 14, opacity: invoicedOnly ? 1 : 0.35 }} />
          Already invoiced ({invoicedCount})
        </ToggleButton>
      </Box>

      <Box
        sx={{
          bgcolor: '#fff',
          borderRadius: '10px',
          border: `1px solid ${LINE_STRONG}`,
          overflow: 'hidden',
        }}
      >
        <Box sx={headerGridSx}>
          {['Date', 'Description', `${subjectCap} share`, 'Collected', 'Outcome', ''].map(
            (label, index) => (
              <Typography
                key={label || 'action'}
                variant="caption"
                sx={{
                  fontSize: '0.6563rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: INK_FAINT,
                  textAlign: index >= 2 && index <= 4 ? 'right' : 'left',
                }}
              >
                {label}
              </Typography>
            ),
          )}
        </Box>

        {visible.map((row, index) =>
          row.kind === 'account' ? (
            <AccountRowLine
              key={row.key}
              row={row}
              divider={index < visible.length - 1}
              onInvoiceReminder={onAccountInvoiceReminder}
              onInvoiceOpen={onAccountInvoiceOpen}
              onInvoiceCopyLink={onInvoiceCopyLink}
              onInvoiceVoid={onInvoiceVoid}
              onInvoiceRetryPayment={onInvoiceRetryPayment}
              onInvoiceStopRetries={onInvoiceStopRetries}
              onAssignCharge={onAssignCharge}
              onReverseCharge={onReverseCharge}
              onRemoveRecordedPayment={onRemoveRecordedPayment}
              onBillHeldCharge={onBillHeldCharge}
              onWriteOffHeldCharge={onWriteOffHeldCharge}
              subject={subjectCap.toLowerCase()}
              canManagePatientFinancials={canManagePatientFinancials}
              applyCreditState={applyCreditFor(row)}
              refundState={recordRefundFor(row)}
              onApplyCredit={onApplyCredit}
              onRecordRefund={onRecordRefund}
            />
          ) : (
            <Row
              key={row.key}
              onReverseCharge={onReverseCharge}
              canManagePatientFinancials={canManagePatientFinancials}
              row={row}
              divider={index < visible.length - 1}
              onOpen={() => openEncounter(row)}
              onAction={(action, options) => onAction(row, action, options)}
              onInvoiceCopyLink={onInvoiceCopyLink}
              onInvoiceVoid={onInvoiceVoid}
              onInvoiceRetryPayment={onInvoiceRetryPayment}
              onInvoiceStopRetries={onInvoiceStopRetries}
              applyCreditState={applyCreditFor(row)}
              refundState={recordRefundFor(row)}
              onApplyCredit={onApplyCredit}
              onRecordRefund={onRecordRefund}
            />
          ),
        )}
        {visible.length === 0 && (
          <Typography variant="body2" sx={{ p: 2, color: INK_FAINT }}>
            {rows.length === 0 ? 'No billing activity yet.' : 'Nothing in this view.'}
          </Typography>
        )}
      </Box>

      {rows.length > 0 && (
        <Typography variant="caption" sx={{ color: INK_FAINT }}>
          Showing {visible.length} of {rows.length} · hover a dotted amount to see
          how it was calculated
        </Typography>
      )}
    </Box>
  );
};

// ─── Row ─────────────────────────────────────────────────────────────────────

// Last column fits the 132px primary button plus the 28px "…" trigger.
const GRID = '96px minmax(0,1fr) 104px 96px 150px 170px';
const headerGridSx = {
  display: 'grid',
  gridTemplateColumns: GRID,
  gap: 1.5,
  px: 2.5,
  py: 0.75,
  borderBottom: `1px solid ${LINE_STRONG}`,
} as const;

/** Handlers a row's credit verbs route to — the source travels with the
 * call so the dialogs know the scope and the capped amount. */
interface CreditVerbHandler {
  (source: CreditSource, options?: { via: 'button' | 'overflow' }): void;
}

const Row = ({
  row,
  divider,
  onOpen,
  onAction,
  onReverseCharge,
  canManagePatientFinancials,
  onInvoiceCopyLink,
  onInvoiceVoid,
  onInvoiceRetryPayment,
  onInvoiceStopRetries,
  applyCreditState,
  refundState,
  onApplyCredit,
  onRecordRefund,
}: {
  row: BalanceRow;
  divider: boolean;
  onOpen: () => void;
  onAction: (action: string, options: { via: 'button' | 'overflow' }) => void;
  onReverseCharge?: (charge: {
    chargeLineId: string;
    amountCents: number;
    hasAppliedPayment?: boolean;
    label: string;
    encounterId?: string;
  }) => void;
  canManagePatientFinancials?: boolean;
  onInvoiceCopyLink?: (invoiceId: string) => void;
  onInvoiceVoid?: (invoiceId: string) => void;
  onInvoiceRetryPayment?: (
    invoiceId: string,
    options?: { via: 'button' | 'overflow' },
  ) => void;
  onInvoiceStopRetries?: (invoiceId: string) => void;
  applyCreditState: CreditVerbState;
  refundState: CreditVerbState;
  onApplyCredit?: CreditVerbHandler;
  onRecordRefund?: CreditVerbHandler;
}) => {
  const tone = TONE_STYLES[row.outcome.tone];
  const actions = rowActionsOf(row, {
    applyCredit: applyCreditState.source,
    applyCreditBlockedReason: applyCreditState.blockedReason,
    recordRefund: refundState.source,
    recordRefundBlockedReason: refundState.blockedReason,
    invoiceCopyLinkAvailable: Boolean(onInvoiceCopyLink),
    invoiceVoidAvailable: Boolean(onInvoiceVoid),
    invoiceRetryPaymentAvailable: Boolean(onInvoiceRetryPayment),
    invoiceStopRetriesAvailable: Boolean(onInvoiceStopRetries),
    canManagePatientFinancials,
  });
  // Its own control, never the row click: the row is role="link" and
  // opening the encounter must stay what clicking it does (gdn-3190).
  const [linesOpen, setLinesOpen] = useState(false);
  const hasLines = row.activity.length > 0;

  const runAction = (key: string, via: 'button' | 'overflow') => {
    if (key === APPLY_CREDIT_KEY && applyCreditState.source)
      onApplyCredit?.(applyCreditState.source, { via });
    else if (key === RECORD_REFUND_KEY && refundState.source)
      onRecordRefund?.(refundState.source, { via });
    else if (key === INVOICE_COPY_LINK_KEY && row.invoice)
      onInvoiceCopyLink?.(row.invoice.invoiceId);
    else if (key === INVOICE_VOID_KEY && row.invoice)
      onInvoiceVoid?.(row.invoice.invoiceId);
    else if (key === INVOICE_RETRY_PAYMENT_KEY && row.invoice)
      onInvoiceRetryPayment?.(row.invoice.invoiceId, { via });
    else if (key === INVOICE_STOP_RETRIES_KEY && row.invoice)
      onInvoiceStopRetries?.(row.invoice.invoiceId);
    else onAction(key, { via });
  };

  return (
    <>
    <Box
      onClick={onOpen}
      onKeyDown={event => {
        if (event.key === 'Enter') onOpen();
      }}
      role="link"
      tabIndex={0}
      aria-label="Open encounter"
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID,
        gap: 1.5,
        alignItems: 'center',
        px: 2.5,
        py: 1,
        cursor: 'pointer',
        borderBottom: divider ? `1px solid ${LINE}` : 'none',
        '&:hover': { bgcolor: BG },
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>
        {formatDate(row.date)}
      </Typography>
      <Tooltip
        placement="top-start"
        slotProps={{ tooltip: { sx: HOVER_TOOLTIP_SX } }}
        title={
          <Typography variant="caption">
            <b>{row.label}</b> — opens the encounter
            {row.invoice ? ` · ${invoiceNoteDetail(row.invoice)}` : ''}
          </Typography>
        }
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            <EventAvailableOutlinedIcon
              sx={{ fontSize: 15, color: INK_FAINT, flexShrink: 0 }}
            />
            <Typography
              variant="body2"
              noWrap
              sx={{
                color: INK_SOFT,
                fontSize: '0.8125rem',
                '&:hover': { color: BRAND, textDecoration: 'underline' },
              }}
            >
              {row.label}
            </Typography>
          </Box>
          <InvoiceNote
            invoice={row.invoice}
            onRetry={
              onInvoiceRetryPayment && row.invoice
                ? () => onInvoiceRetryPayment(row.invoice!.invoiceId, { via: 'button' })
                : undefined
            }
          />
        </Box>
      </Tooltip>
      <AmountCell
        text={row.share?.text ?? null}
        hover={row.receipt ? <Receipt rows={row.receipt} /> : null}
      />
      <AmountCell
        text={row.collectedCents > 0 ? formatCents(row.collectedCents) : null}
        hover={row.activity.length > 0 ? <Activity lines={row.activity} /> : null}
      />
      <Box sx={{ textAlign: 'right' }}>
        <Tooltip
          placement="left"
          slotProps={{ tooltip: { sx: HOVER_TOOLTIP_SX } }}
          title={
            row.reconciliation ? (
              <Typography variant="caption">
                <b>{row.reconciliation.headline}</b> {row.reconciliation.explanation}
              </Typography>
            ) : (
              ''
            )
          }
        >
          <Box component="span">
            {tone.bg ? (
              <Chip
                size="small"
                label={row.outcome.label}
                sx={{ fontWeight: 700, color: tone.color, bgcolor: tone.bg }}
              />
            ) : (
              <Typography
                variant="caption"
                sx={{ color: tone.color, fontWeight: 600 }}
              >
                {row.outcome.label}
              </Typography>
            )}
          </Box>
        </Tooltip>
      </Box>
      <RowActionsCell
        actions={actions}
        rowLabel={row.label}
        onRun={runAction}
        disclosure={
          hasLines ? (
            <IconButton
              size="small"
              aria-label={
                linesOpen
                  ? `Hide charge lines for ${row.label}`
                  : `Show charge lines for ${row.label}`
              }
              aria-expanded={linesOpen}
              onClick={event => {
                event.stopPropagation();
                setLinesOpen(open => !open);
              }}
              sx={{ color: INK_FAINT, p: 0.25 }}
            >
              {linesOpen ? (
                <ExpandLessIcon sx={{ fontSize: 18 }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          ) : null
        }
      />
    </Box>
    {linesOpen && (
      // Rendered regardless of collectedCents: the Collected hover shows
      // the same data but only when something has been collected, which
      // is precisely when the duplicate-charge case ISN'T (gdn-3190).
      <EncounterLinesPanel
        lines={row.activity}
        encounterId={row.encounterId}
        onReverseCharge={onReverseCharge}
      />
    )}
    </>
  );
};

const actionButtonSx = {
  textTransform: 'none',
  fontSize: '0.6875rem',
  fontWeight: 600,
  py: 0.25,
  whiteSpace: 'nowrap',
  // One fixed width for every row action ("Update card & retry" is the
  // widest label) — a ragged action column reads as different kinds of
  // controls.
  width: 132,
} as const;

/** One primary button plus a "…" overflow for the row's remaining verbs
 * (gdn-2979) — same kebab grammar as the Open Invoices card. Lives inside
 * cells that stop click/keydown propagation; the Menu portals out of the
 * row's DOM but React synthetic events still bubble through the tree, so
 * every menu handler stops propagation too — Enter on a menu item must
 * never double into row navigation. */
const RowActionsCell = ({
  disclosure,
  actions,
  rowLabel,
  onRun,
}: {
  /** Expand/collapse control for the row's charge lines (gdn-3190),
   * rendered alongside the verbs rather than on the row itself so the
   * row's link semantics survive. */
  disclosure?: React.ReactNode;
  actions: RowActions;
  rowLabel: string;
  onRun: (key: string, via: 'button' | 'overflow') => void;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { primary, menu } = actions;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.25,
      }}
      onClick={event => event.stopPropagation()}
      onKeyDown={event => event.stopPropagation()}
    >
      {disclosure}
      {primary && (
        <Button
          size="small"
          variant="contained"
          onClick={() => onRun(primary.key, 'button')}
          aria-label={primary.ariaLabel}
          sx={actionButtonSx}
        >
          {primary.label}
        </Button>
      )}
      {menu.length > 0 && (
        <>
          <IconButton
            size="small"
            aria-label={`More actions for ${rowLabel}`}
            onClick={event => setAnchorEl(event.currentTarget)}
            sx={{ color: INK_FAINT, p: 0.25 }}
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            onClick={event => event.stopPropagation()}
            onKeyDown={event => event.stopPropagation()}
          >
            {menu.map(item =>
              item.blockedReason ? (
                // aria-disabled, never the `disabled` prop: a disabled
                // MenuItem is not focusable, so keyboard and screen-reader
                // users would reach neither the item nor its reason — the
                // explanation would exist for mouse users only.
                <MenuItem
                  key={item.key}
                  aria-disabled
                  aria-label={`${item.label} — unavailable. ${item.blockedReason}`}
                  onClick={event => event.preventDefault()}
                  sx={{
                    fontSize: '0.8125rem',
                    display: 'block',
                    cursor: 'default',
                    color: INK_FAINT,
                    '&:hover': { bgcolor: 'transparent' },
                  }}
                >
                  {item.label}
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: INK_FAINT, fontSize: '0.6875rem' }}
                  >
                    {item.blockedReason}
                  </Typography>
                </MenuItem>
              ) : (
                <MenuItem
                  key={item.key}
                  aria-label={item.ariaLabel}
                  sx={{ fontSize: '0.8125rem' }}
                  onClick={() => {
                    setAnchorEl(null);
                    onRun(item.key, 'overflow');
                  }}
                >
                  {item.label}
                </MenuItem>
              ),
            )}
          </Menu>
        </>
      )}
    </Box>
  );
};

/** A non-encounter ledger entry in the same timeline (prototype round's
 * "different kind of row" grammar): muted receipt glyph + positive kind
 * label, one signed amount, no lifecycle hovers, no navigation. */
const AccountRowLine = ({
  row,
  divider,
  onInvoiceReminder,
  onInvoiceOpen,
  onInvoiceCopyLink,
  onInvoiceVoid,
  onInvoiceRetryPayment,
  onInvoiceStopRetries,
  onAssignCharge,
  onReverseCharge,
  onRemoveRecordedPayment,
  onBillHeldCharge,
  onWriteOffHeldCharge,
  subject,
  canManagePatientFinancials,
  applyCreditState,
  refundState,
  onApplyCredit,
  onRecordRefund,
}: {
  row: AccountRow;
  divider: boolean;
  onInvoiceReminder?: (invoiceId: string) => void;
  onInvoiceOpen?: (invoiceId: string) => void;
  onInvoiceCopyLink?: (invoiceId: string) => void;
  onInvoiceVoid?: (invoiceId: string) => void;
  onInvoiceRetryPayment?: (
    invoiceId: string,
    options?: { via: 'button' | 'overflow' },
  ) => void;
  onInvoiceStopRetries?: (invoiceId: string) => void;
  onAssignCharge?: (charge: { chargeLineId: string; amountCents: number; label: string }) => void;
  canManagePatientFinancials?: boolean;
  onRemoveRecordedPayment?: (payments: CreditSourceEntry[]) => void;
  onBillHeldCharge?: (charge: HeldChargeTarget) => void;
  onWriteOffHeldCharge?: (charge: HeldChargeTarget) => void;
  subject: string;
  onReverseCharge?: (charge: {
    chargeLineId: string;
    amountCents: number;
    hasAppliedPayment?: boolean;
    label: string;
    encounterId?: string;
  }) => void;
  applyCreditState: CreditVerbState;
  refundState: CreditVerbState;
  onApplyCredit?: CreditVerbHandler;
  onRecordRefund?: CreditVerbHandler;
}) => {
  // Capture the id once: the row's invoice is optional, and narrowing it
  // here keeps both handlers honest without an assertion.
  const invoiceLinkId = row.invoice && onInvoiceOpen ? row.invoice.invoiceId : null;
  const linksToInvoice = invoiceLinkId !== null;
  const assignableCharge = assignableChargeOf(row);
  const reversibleCharge = reversibleChargeOf(row);
  const removablePayments = removablePaymentsOf(row);
  const heldCharge = heldChargeOf(row);
  const actions = rowActionsOf(row, {
    applyCredit: applyCreditState.source,
    applyCreditBlockedReason: applyCreditState.blockedReason,
    recordRefund: refundState.source,
    recordRefundBlockedReason: refundState.blockedReason,
    accountReminderAvailable: Boolean(onInvoiceReminder),
    invoiceCopyLinkAvailable: Boolean(onInvoiceCopyLink),
    invoiceVoidAvailable: Boolean(onInvoiceVoid),
    invoiceRetryPaymentAvailable: Boolean(onInvoiceRetryPayment),
    invoiceStopRetriesAvailable: Boolean(onInvoiceStopRetries),
    assignChargeAvailable: Boolean(onAssignCharge) && assignableCharge !== null,
    reverseChargeAvailable: Boolean(onReverseCharge) && reversibleCharge !== null,
    canManagePatientFinancials,
    removeRecordedPaymentAvailable:
      Boolean(onRemoveRecordedPayment) && removablePayments.length > 0,
    heldChargeDecisionAvailable: Boolean(onBillHeldCharge && onWriteOffHeldCharge),
    subject,
  });
  const runAction = (key: string, via: 'button' | 'overflow') => {
    if (key === ASSIGN_CHARGE_KEY && assignableCharge)
      onAssignCharge?.({ ...assignableCharge, label: row.label });
    if (key === REMOVE_RECORDED_PAYMENT_KEY && removablePayments.length > 0)
      onRemoveRecordedPayment?.(removablePayments);
    if (key === REVERSE_CHARGE_KEY && reversibleCharge)
      onReverseCharge?.({ ...reversibleCharge, label: row.label });
    else if (key === APPLY_CREDIT_KEY && applyCreditState.source)
      onApplyCredit?.(applyCreditState.source, { via });
    else if (key === RECORD_REFUND_KEY && refundState.source)
      onRecordRefund?.(refundState.source, { via });
    else if (key === ACCOUNT_REMINDER_KEY && row.invoice)
      onInvoiceReminder?.(row.invoice.invoiceId);
    else if (key === INVOICE_COPY_LINK_KEY && row.invoice)
      onInvoiceCopyLink?.(row.invoice.invoiceId);
    else if (key === INVOICE_VOID_KEY && row.invoice)
      onInvoiceVoid?.(row.invoice.invoiceId);
    else if (key === INVOICE_RETRY_PAYMENT_KEY && row.invoice)
      onInvoiceRetryPayment?.(row.invoice.invoiceId, { via });
    else if (key === INVOICE_STOP_RETRIES_KEY && row.invoice)
      onInvoiceStopRetries?.(row.invoice.invoiceId);
    else if (key === BILL_HELD_CHARGE_KEY && heldCharge)
      onBillHeldCharge?.({ ...heldCharge, label: row.label });
    else if (key === WRITE_OFF_HELD_CHARGE_KEY && heldCharge)
      onWriteOffHeldCharge?.({ ...heldCharge, label: row.label });
  };
  return (
  <Box
    onClick={
      invoiceLinkId ? () => onInvoiceOpen?.(invoiceLinkId) : undefined
    }
    onKeyDown={
      linksToInvoice
        ? event => {
            if (event.key === 'Enter') onInvoiceOpen?.(invoiceLinkId);
          }
        : undefined
    }
    role={linksToInvoice ? 'link' : undefined}
    tabIndex={linksToInvoice ? 0 : undefined}
    aria-label={linksToInvoice ? 'Open invoice' : undefined}
    sx={{
      display: 'grid',
      gridTemplateColumns: GRID,
      gap: 1.5,
      alignItems: 'center',
      px: 2.5,
      py: 1,
      borderBottom: divider ? `1px solid ${LINE}` : 'none',
      ...(linksToInvoice && {
        cursor: 'pointer',
        '&:hover': { bgcolor: BG },
      }),
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>
      {formatDate(row.date)}
    </Typography>
    <Tooltip
      placement="top-start"
      slotProps={{ tooltip: { sx: HOVER_TOOLTIP_SX } }}
      title={
        linksToInvoice ? (
          <Typography variant="caption">
            <b>{row.label}</b> — opens the invoice
          </Typography>
        ) : (
          ''
        )
      }
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <ReceiptLongOutlinedIcon
            sx={{ fontSize: 15, color: INK_FAINT, flexShrink: 0 }}
          />
          <Typography
            variant="body2"
            noWrap
            sx={{
              color: INK_SOFT,
              fontSize: '0.8125rem',
              ...(linksToInvoice && {
                '&:hover': { color: BRAND, textDecoration: 'underline' },
              }),
            }}
          >
            {row.label}
          </Typography>
        </Box>
        {/* Only when the row's own detail hasn't already said it — the
            rollup writes "Invoiced to the patient." into detail. A decline
            always shows: "Invoiced" and "the card was declined" are
            different facts, and the second is the one that decides what
            happens next (gdn-3064). */}
        {(row.invoice?.decline ||
          !row.detail?.toLowerCase().startsWith('invoiced')) && (
          <InvoiceNote
            invoice={row.invoice}
            onRetry={
              onInvoiceRetryPayment && row.invoice
                ? () => onInvoiceRetryPayment(row.invoice!.invoiceId, { via: 'button' })
                : undefined
            }
          />
        )}
      </Box>
    </Tooltip>
    {row.column === 'share' ? (
      <>
        <Box sx={{ textAlign: 'right' }}>
          <AccountAmount row={row} />
        </Box>
        <AmountCell text={null} hover={null} />
      </>
    ) : (
      <>
        <AmountCell text={null} hover={null} />
        <Box sx={{ textAlign: 'right' }}>
          <AccountAmount row={row} />
        </Box>
      </>
    )}
    <Box sx={{ textAlign: 'right', minWidth: 0 }}>
      {row.detail && (
        <Tooltip
          placement="left"
          slotProps={{ tooltip: { sx: HOVER_TOOLTIP_SX } }}
          title={<Typography variant="caption">{row.detail}</Typography>}
        >
          <Typography
            variant="caption"
            noWrap
            sx={{ display: 'block', color: INK_FAINT, fontWeight: 600 }}
          >
            {row.detail}
          </Typography>
        </Tooltip>
      )}
    </Box>
    <RowActionsCell actions={actions} rowLabel={row.label} onRun={runAction} />
  </Box>
  );
};

/** Account row amount — sign + tone colored, rendered into whichever
 * column the row's money belongs to (share for charges, collected for
 * payments/credits). When the row carries source events (an unapplied
 * credit's payments/reversals), the amount is a dotted hover target
 * showing where the money came from. */
const AccountAmount = ({ row }: { row: AccountRow }) => {
  const amount = (
    <Typography
      component="span"
      variant="body2"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.8125rem',
        fontWeight: 600,
        color:
          row.amountCents < 0 || row.tone === 'ok'
            ? TONE_STYLES.credit.color
            : row.tone === 'info'
              ? INK_FAINT
              : TONE_STYLES.owes.color,
        ...(row.sources.length > 0 && {
          borderBottom: `1px dotted ${INK_FAINT}`,
          cursor: 'help',
        }),
      }}
    >
      {row.amountCents < 0 ? MINUS : ''}
      {formatCents(Math.abs(row.amountCents))}
    </Typography>
  );
  if (row.sources.length === 0) return amount;
  return (
    <Tooltip
      placement="left"
      slotProps={{ tooltip: { sx: HOVER_TOOLTIP_SX } }}
      title={<AccountSources sources={row.sources} />}
    >
      {amount}
    </Tooltip>
  );
};

/** The collapsed contra pair, annotated: the payments and reversals that
 * left this credit on the account. History, not arithmetic — a payment
 * that settled a later-reversed charge appears alongside the reversal,
 * so these need not sum to the credit. */
const AccountSources = ({ sources }: { sources: AccountRow['sources'] }) => (
  <Box sx={{ minWidth: 240 }}>
    <Typography
      variant="caption"
      sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}
    >
      How this credit adds up
    </Typography>
    {sources.map((src, index) => (
      <Box
        key={`${src.label}-${index}`}
        sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}
      >
        <Typography variant="caption" sx={{ color: INK_SOFT }}>
          {formatDate(src.date)} · {src.label}
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontFamily: 'monospace', fontWeight: 600 }}
        >
          {formatCents(src.amountCents)}
        </Typography>
      </Box>
    ))}
  </Box>
);

/** Amount cell: dotted-underline hover target only when a derivation
 * exists; plain text otherwise; muted dash when empty. */
const AmountCell = ({
  text,
  hover,
}: {
  text: string | null;
  hover: React.ReactNode | null;
}) => (
  <Box sx={{ textAlign: 'right' }}>
    {text === null ? (
      <Typography variant="body2" sx={{ color: INK_FAINT }}>
        —
      </Typography>
    ) : hover ? (
      <Tooltip
        placement="left"
        slotProps={{ tooltip: { sx: HOVER_TOOLTIP_SX } }}
        title={hover}
      >
        <Typography
          component="span"
          variant="body2"
          sx={{
            fontFamily: 'monospace',
            fontSize: '0.8125rem',
            fontWeight: 600,
            borderBottom: `1px dotted ${INK_FAINT}`,
            cursor: 'help',
          }}
        >
          {text}
        </Typography>
      </Tooltip>
    ) : (
      <Typography
        component="span"
        variant="body2"
        sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', fontWeight: 600 }}
      >
        {text}
      </Typography>
    )}
  </Box>
);

const Receipt = ({ rows }: { rows: ReceiptRow[] }) => (
  <Box sx={{ minWidth: 220 }}>
    {rows.map((row, index) => {
      const last = index === rows.length - 1;
      return (
        <Box
          key={row.label}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            borderTop: last ? `1px solid ${LINE_STRONG}` : 'none',
            mt: last ? 0.25 : 0,
            pt: last ? 0.25 : 0,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: last ? INK : INK_SOFT, fontWeight: last ? 700 : 400 }}
          >
            {row.label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontFamily: 'monospace',
              fontWeight: last ? 700 : 400,
              color: row.deduction ? '#b91c1c' : last ? INK : INK_SOFT,
            }}
          >
            {row.deduction ? MINUS : ''}
            {formatCents(row.amountCents)}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const Activity = ({ lines }: { lines: ActivityLine[] }) => (
  <Box sx={{ minWidth: 240 }}>
    {lines.map(line => (
      <Box
        key={line.id}
        sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.25 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
            {line.tone === 'ok' ? '✓ ' : ''}
            {line.label}
          </Typography>
          {line.detail && (
            <Typography
              variant="caption"
              sx={{ display: 'block', fontSize: '0.6563rem', color: INK_FAINT }}
            >
              {line.detail}
            </Typography>
          )}
        </Box>
        <Typography
          variant="caption"
          sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}
        >
          {formatCents(line.amount_cents)}
        </Typography>
      </Box>
    ))}
  </Box>
);

const formatDate = (date: string | null): string => {
  if (!date) return '—';
  const parsed = new Date(`${date}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return date;
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};
